import { useCallback, useEffect, useRef, useState } from "react";
import axios from "axios";
import { API } from "@/lib/api";

/**
 * De connectors van het plusmenu, per gesprek. I7, 17 oktober 2026.
 *
 * In een gesprek komt de stand van de server (`/conversations/{id}/connectors`)
 * en gaat een klik daar ook heen. Op het welkomstscherm bestaat er nog geen
 * gesprek: dan staat de keuze hier als WACHTEND, en `pasToe` schrijft haar weg
 * zodra het eerste bericht het gesprek heeft aangemaakt. Een gesprek kan een
 * connector alleen UITzetten; wat in Instellingen uit staat, blijft uit.
 *
 * I8: naast de MCP-servers (`soort: "mcp"`) staan de bestaande koppelingen erin
 * (`soort: "bron"`: Google, Nextcloud, Fathom, TASQ). De lijst komt in beide
 * gevallen van de server (`/connectors` zonder gesprek), zodat de indeling
 * (Google is agenda, Drive en Gmail samen) op een plek staat.
 */

/** De sleutel van een keuze: soort en id samen, want "google" en een server-id
 *  leven in dezelfde lijst. PUUR. */
export const sleutelVan = (soort, id) => `${soort || "mcp"}:${id}`;

/** De lijst zonder gesprek, met wat er voor het nieuwe gesprek is gekozen. Een
 *  keuze zet niets aan wat in Instellingen uit staat. PUUR. */
export function metWachtend(lijst, wachtend) {
  return (lijst || []).map(c => ({
    ...c, aan_hier: !!c.aan_hier && wachtend[sleutelVan(c.soort, c.id)] !== false,
  }));
}

export function useGesprekConnectors(conversationId) {
  const [connectors, setConnectors] = useState([]);
  const [rol, setRol] = useState("");
  const wachtend = useRef({});
  const servers = useRef([]);

  const laad = useCallback(async () => {
    try {
      if (conversationId) {
        const r = await axios.get(`${API}/conversations/${conversationId}/connectors`);
        setConnectors(Array.isArray(r.data) ? r.data : []);
      } else {
        const r = await axios.get(`${API}/connectors`);
        servers.current = Array.isArray(r.data) ? r.data : [];
        setConnectors(metWachtend(servers.current, wachtend.current));
      }
    } catch {
      setConnectors([]);
    }
  }, [conversationId]);

  useEffect(() => { laad(); }, [laad]);
  useEffect(() => {
    axios.get(`${API}/organisatie`).then(o => setRol(o?.data?.mijn_rol || "")).catch(() => setRol(""));
  }, []);

  const zet = useCallback(async (id, aan, soort = "mcp") => {
    if (conversationId) {
      const r = await axios.put(`${API}/conversations/${conversationId}/connectors/${soort}/${id}`, { aan });
      setConnectors(Array.isArray(r.data) ? r.data : []);
      return;
    }
    wachtend.current = { ...wachtend.current, [sleutelVan(soort, id)]: aan };
    setConnectors(metWachtend(servers.current, wachtend.current));
  }, [conversationId]);

  /** Na het aanmaken van een gesprek: de keuze van het welkomstscherm erin. */
  const pasToe = useCallback(async (convId) => {
    const keuzes = Object.entries(wachtend.current);
    wachtend.current = {};
    for (const [sleutel, aan] of keuzes) {
      const [soort, ...rest] = sleutel.split(":");
      try {
        await axios.put(`${API}/conversations/${convId}/connectors/${soort}/${rest.join(":")}`, { aan });
      } catch {
        // Een server die intussen weg is, kan niet meer worden omgezet.
      }
    }
  }, []);

  return { connectors, rol, zet, pasToe, laad };
}
